import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { dashboardApi, discussionsApi } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Avatar } from '@/components/ui/Avatar';
import { StatusDropdown, DiscussionStatus } from '@/components/ui/StatusDropdown';
import { formatRelativeTime } from '@/lib/utils';

interface AgencyQueueCardProps {
  maxItems?: number;
}

export function AgencyQueueCard({ maxItems = 5 }: AgencyQueueCardProps) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['agency-queue'],
    queryFn: () => dashboardApi.getAgencyQueue(),
    staleTime: 30000, // 30 seconds
  });

  // Status update mutation
  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: DiscussionStatus }) =>
      discussionsApi.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency-queue'] });
    },
  });

  const handleStatusChange = (id: string, status: DiscussionStatus) => {
    statusMutation.mutate({ id, status });
  };

  const items = data?.items.slice(0, maxItems) || [];
  const total = data?.total || 0;
  const hasMore = total > maxItems;

  // Don't render if no items (non-agency users or no waiting_on_solid items)
  if (!isLoading && items.length === 0) {
    return null;
  }

  return (
    <Card padding="none">
      <div className="flex items-center justify-between px-4 py-3 border-b border-grey-100 bg-warning/5">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-warning" />
          <span className="text-sm font-medium text-secondary">Discussions Waiting on Solid</span>
        </div>
        {total > 0 && (
          <span className="text-xs font-medium text-warning bg-warning/10 px-2 py-0.5 rounded-full">
            {total}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 text-grey-400 animate-spin" />
        </div>
      ) : (
        <>
          <div className="divide-y divide-grey-100">
            {items.map((item) => (
              <div key={item.id} className="p-4 hover:bg-grey-50/50 transition-colors">
                <div className="flex items-start gap-3">
                  <Avatar
                    src={item.author.avatarUrl}
                    name={item.author.name}
                    size="sm"
                    className="shrink-0 mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/projects/${item.project.id}/discussions/${item.id}`}
                      className="text-sm font-medium text-secondary hover:text-accent transition-colors line-clamp-1"
                    >
                      {item.title}
                    </Link>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-grey-500">{item.project.name}</span>
                      <span className="text-xs text-grey-300">·</span>
                      <span className="text-xs text-grey-400">
                        {formatRelativeTime(item.updatedAt)}
                      </span>
                    </div>
                    {item.lastReplyPreview && (
                      <p className="text-xs text-grey-500 mt-1.5 line-clamp-2">
                        "{item.lastReplyPreview}..."
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1 text-xs text-warning">
                        <Clock className="w-3 h-3" />
                        Waiting {item.waitingDays === 0 ? 'today' : `${item.waitingDays}d`}
                      </div>
                      <StatusDropdown
                        status={item.discussionStatus as DiscussionStatus}
                        onChange={(status) => handleStatusChange(item.id, status)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="px-4 py-3 border-t border-grey-100">
              <Link
                to="/projects?filter=waiting-on-solid"
                className="text-sm font-medium text-accent hover:text-accent-700 flex items-center gap-1"
              >
                View all {total} items <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
